import {formatUserDefinedObjectAdditionalColumnValue} from "~/domainModels/userDefinedObjectAdditionalColumnValues";
import {
    UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType,
    type UserDefinedObjectTemplateAdditionalColumn as UdoColumn,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {DateTime} from "luxon";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";

import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";

import {
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_AGE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID,
    EmotoradEventTypes,
    getEmotoradEventTypeSelectOptions,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {BtlActivity} from "~/domainModels/btlActivities";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {OttopilotEventContainerTableViewColumn} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTableViewColumn";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {
    emotoradJourneyTestRideCountCellString,
    isEmotoradJourneyTestRideCountColumnField,
    isEmotoradTestRideCountJourneyColumnDisplayName,
} from "~/utilities/emotoradJourneyTestRideCount";
import {getUserDisplayName, getUserRole, type PlatformUserDisplayDetails} from "~/utilities/utilities";
import {isGeographyAdditionalColumnType, resolveGeographyAdditionalColumnValue} from "~/utilities/geographyValueResolver";

function formatDurationSinceInstant(instant: DateTime): string {
    const now = DateTime.now();
    if (!instant.isValid) {
        return "-";
    }
    if (instant > now) {
        return "0 min";
    }
    const totalDays = now.diff(instant, "days").as("days");
    if (totalDays >= 1) {
        const days = Math.floor(totalDays);
        return `${days} day${days === 1 ? "" : "s"}`;
    }
    const totalHours = now.diff(instant, "hours").as("hours");
    if (totalHours >= 1) {
        const hours = Math.floor(totalHours);
        return `${hours} hr${hours === 1 ? "" : "s"}`;
    }
    const minutes = Math.max(0, Math.floor(now.diff(instant, "minutes").as("minutes")));
    return `${minutes} min`;
}

function emotoradEventTemplateIdToDisplayName(templateId: string, eventTemplates: Array<OttopilotEventTemplate>): string | null {
    const normalized = templateId.trim();
    if (normalized === "") {
        return null;
    }
    const fromOptions = getEmotoradEventTypeSelectOptions().find((item) => String(item.id) === normalized);
    if (fromOptions !== undefined) {
        return fromOptions.displayName;
    }
    const fromTemplate = eventTemplates.find((item) => String(item.id) === normalized);
    if (fromTemplate !== undefined) {
        return fromTemplate.displayName;
    }
    return null;
}

/** Last Activity stores an Event Template id; fall back to the newest Event Container event. */
export function resolveEmotoradLastActivityEventDisplay(eventContainer: EventContainerWithEvents, eventTemplates: Array<OttopilotEventTemplate>): string {
    const raw = eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID];
    if (raw !== null && raw !== undefined) {
        const asText = typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean" ? String(raw).trim() : "";
        if (asText !== "") {
            const fromColumn = emotoradEventTemplateIdToDisplayName(asText, eventTemplates);
            if (fromColumn !== null) {
                return fromColumn;
            }
            // Legacy rows: ISO timestamp was stored on this column (should be on Last Activity On).
            if (DateTime.fromISO(asText).isValid !== true) {
                return asText;
            }
        }
    }

    let latestEvent: EventContainerWithEvents["events"][number] | null = null;
    let latestMs = -1;
    for (const event of eventContainer.events) {
        for (const iso of [event.updatedAt, event.createdAt]) {
            const ms = Date.parse(iso);
            if (Number.isNaN(ms) === true || ms <= latestMs) {
                continue;
            }
            latestMs = ms;
            latestEvent = event;
        }
    }
    if (latestEvent !== null) {
        return emotoradEventTemplateIdToDisplayName(String(latestEvent.templateId), eventTemplates) ?? "-";
    }

    const legacyActivityRaw = eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID];
    if (typeof legacyActivityRaw === "string" && DateTime.fromISO(legacyActivityRaw.trim()).isValid === true) {
        return emotoradEventTemplateIdToDisplayName(String(EmotoradEventTypes.lead), eventTemplates) ?? "Lead";
    }

    return "-";
}

/** Emotorad Event Container table derives this synthetic field when the backend omits it. */
export function resolveEmotoradLastActivityAgeDisplay(eventContainer: EventContainerWithEvents): string {
    const raw = eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_AGE__COLUMN_ID];
    if (raw !== undefined && raw !== null) {
        if (typeof raw === "number" && Number.isFinite(raw)) {
            return String(raw);
        }
        if (typeof raw === "string") {
            const trimmed = raw.trim();
            if (trimmed !== "" && trimmed !== "-") {
                const asInstant = DateTime.fromISO(trimmed);
                if (asInstant.isValid) {
                    return formatDurationSinceInstant(asInstant);
                }
                return trimmed;
            }
        }
    }

    const lastOn = eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID];
    if (typeof lastOn === "string" && lastOn.trim() !== "") {
        const lastOnDt = DateTime.fromISO(lastOn);
        if (lastOnDt.isValid) {
            return formatDurationSinceInstant(lastOnDt);
        }
    }

    const lastActivityColumnRaw = eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID];
    if (typeof lastActivityColumnRaw === "string" && lastActivityColumnRaw.trim() !== "") {
        const legacyInstant = DateTime.fromISO(lastActivityColumnRaw.trim());
        if (legacyInstant.isValid) {
            return formatDurationSinceInstant(legacyInstant);
        }
    }

    let latest: DateTime | null = null;
    const consider = (iso: string): void => {
        const dt = DateTime.fromISO(iso);
        if (!dt.isValid) {
            return;
        }
        if (latest === null || dt > latest) {
            latest = dt;
        }
    };
    consider(eventContainer.updatedAt);
    consider(eventContainer.createdAt);
    for (const ev of eventContainer.events) {
        consider(ev.updatedAt);
        consider(ev.createdAt);
    }
    if (latest === null) {
        return "-";
    }
    return formatDurationSinceInstant(latest);
}

export type EventContainerTableViewColumnTransformDeps = {
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    btlActivities: Array<BtlActivity>;
    formatDateTime: (value: string) => string;
    getOuDisplayName?: (ouId: Uuid) => string;
    getProductCatalogueDisplayName?: (productCatalogueId: Uuid) => string;
    eventContainerAdditionalColumns: Array<UdoColumn>;
    eventTemplates: Array<OttopilotEventTemplate>;
    /** Emotorad Lifecycle Overview: show RM instead of backend ZSM label for this eventContainer column only. */
    emotoradLifecycleZsmColumnDisplayName?: string;
    userDefinedObjectOptionsByTemplateId?: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>;
};

/**
 * Converts an array of OttopilotEventContainerTableViewColumn config entries into OttopilotColumn definitions
 * with the appropriate valueGetter and valueFormatter derived from each column's type.
 */
export function eventContainerTableViewColumnsToOttopilotColumns(
    getAdditionalColumns: CapabilityUdoColumnResolver,
    columns: Array<OttopilotEventContainerTableViewColumn>,
    deps: EventContainerTableViewColumnTransformDeps,
): Array<OttopilotColumn<EventContainerWithEvents>> {
    const {
        users,
        userTemplates,
        platformUserDetailsByUserId,
        formatDateTime,
        getOuDisplayName,
        getProductCatalogueDisplayName,
        eventContainerAdditionalColumns,
        eventTemplates,
        emotoradLifecycleZsmColumnDisplayName,
        userDefinedObjectOptionsByTemplateId,
    } = deps;

    return columns.map((col): OttopilotColumn<EventContainerWithEvents> => {
        const resolvedDisplayName =
            emotoradLifecycleZsmColumnDisplayName !== undefined &&
            col.isAdditionalColumn === true &&
            col.columnType === "OttopilotEventContainer" &&
            (col.field as Uuid) === EMOTORAD__EVENT_CONTAINER__ZSM_NAME__COLUMN_ID
                ? emotoradLifecycleZsmColumnDisplayName
                : col.displayName;
        const base = {
            id: col.id,
            displayName: resolvedDisplayName,
            // TODO: Support sorting Geography columns by their resolved display values.
            sortingCapability: isGeographyAdditionalColumnType(col.type) ? false : col.sortingCapability,
            filteringCapability_contains: col.filteringCapability_contains,
        };
        const columnFieldId = col.field as Uuid;

        if (col.isAdditionalColumn) {
            switch (col.columnType) {
                case "OttopilotEventContainer": {
                    if (columnFieldId === EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_AGE__COLUMN_ID) {
                        return {
                            ...base,
                            type: OttopilotColumnType.string,
                            valueGetter: (eventContainer: EventContainerWithEvents): string => resolveEmotoradLastActivityAgeDisplay(eventContainer),
                        };
                    }
                    if (columnFieldId === EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID) {
                        return {
                            ...base,
                            type: OttopilotColumnType.string,
                            valueGetter: (eventContainer: EventContainerWithEvents): string =>
                                resolveEmotoradLastActivityEventDisplay(eventContainer, eventTemplates),
                        };
                    }
                    if (
                        isEmotoradJourneyTestRideCountColumnField(columnFieldId) === true ||
                        isEmotoradTestRideCountJourneyColumnDisplayName(col.displayName) === true
                    ) {
                        return {
                            ...base,
                            type: OttopilotColumnType.string,
                            valueGetter: (eventContainer: EventContainerWithEvents): string =>
                                emotoradJourneyTestRideCountCellString(eventContainer.additionalColumnValues, columnFieldId),
                        };
                    }
                    switch (col.type) {
                        case UdoColumnType.string:
                        case UdoColumnType.indianPhoneNumber:
                        case UdoColumnType.email:
                        case UdoColumnType.uuid: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string =>
                                    (eventContainer.additionalColumnValues[columnFieldId] as string | undefined | null) ?? "-",
                            };
                        }

                        case UdoColumnType.geographyCountries:
                        case UdoColumnType.geographyIndiaCities:
                        case UdoColumnType.geographyIndiaPincodes:
                        case UdoColumnType.geographyIndiaStates:
                        case UdoColumnType.geographyIndiaZones: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string =>
                                    (eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null) ?? "",
                                valueFormatter: (value: string): string => resolveGeographyAdditionalColumnValue(col.type, value) ?? "-",
                            };
                        }

                        case UdoColumnType.geographyLatitude:
                        case UdoColumnType.geographyLongitude: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const value = eventContainer.additionalColumnValues[columnFieldId];
                                    return typeof value === "number" || typeof value === "string" ? String(value) : "";
                                },
                                valueFormatter: (value: string): string => resolveGeographyAdditionalColumnValue(col.type, value) ?? "-",
                            };
                        }

                        case UdoColumnType.integer:
                        case UdoColumnType.number: {
                            return {
                                ...base,
                                type: OttopilotColumnType.number,
                                valueGetter: (eventContainer: EventContainerWithEvents): number => {
                                    const value = eventContainer.additionalColumnValues[columnFieldId];
                                    if (typeof value === "number" && Number.isFinite(value)) {
                                        return value;
                                    }
                                    if (typeof value === "string" && value.trim() !== "") {
                                        const parsed = Number(value);
                                        if (Number.isNaN(parsed) !== true) {
                                            return parsed;
                                        }
                                    }
                                    return 0;
                                },
                            };
                        }

                        case UdoColumnType.timestamp: {
                            return {
                                ...base,
                                type: OttopilotColumnType.iso8601DateTime,
                                valueGetter: (eventContainer: EventContainerWithEvents): Iso8601DateTime => {
                                    const value = eventContainer.additionalColumnValues[columnFieldId];
                                    return (typeof value === "string" ? value : undefined) as Iso8601DateTime;
                                },
                                valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                            };
                        }

                        case UdoColumnType.user: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const userId = eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (userId === undefined || userId === null) {
                                        return "";
                                    }
                                    return String(userId).trim();
                                },
                                valueFormatter: (value: string): string => {
                                    const trimmed = value.trim();
                                    if (trimmed === "") {
                                        return "-";
                                    }
                                    const name = getUserDisplayName(trimmed as Uuid, users, userTemplates, platformUserDetailsByUserId);
                                    return name;
                                },
                            };
                        }

                        case UdoColumnType.userTemplate: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const userId = eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (userId === undefined || userId === null) {
                                        return "-";
                                    }
                                    return getUserRole(userId, userTemplates);
                                },
                            };
                        }

                        case UdoColumnType.userDefinedObject: {
                            const eventContainerColumn = eventContainerAdditionalColumns.find((item) => item.id === columnFieldId);
                            if (eventContainerColumn?.type !== UdoColumnType.userDefinedObject) {
                                return {...base, type: OttopilotColumnType.string, valueGetter: () => ""};
                            }
                            const options = userDefinedObjectOptionsByTemplateId?.get(eventContainerColumn.configuration.userDefinedObjectTemplateId) ?? [];
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const value = eventContainer.additionalColumnValues[columnFieldId];
                                    return typeof value === "string" ? value : "";
                                },
                                valueFormatter: (value: string): string => options.find((option) => option.id === value)?.displayName ?? "-",
                            };
                        }

                        case UdoColumnType.userDefinedObjects: {
                            const eventContainerColumn = eventContainerAdditionalColumns.find((item) => item.id === columnFieldId);
                            if (eventContainerColumn?.type !== UdoColumnType.userDefinedObjects) {
                                return {...base, type: OttopilotColumnType.array, valueGetter: () => []};
                            }
                            const options = userDefinedObjectOptionsByTemplateId?.get(eventContainerColumn.configuration.userDefinedObjectTemplateId) ?? [];
                            return {
                                ...base,
                                type: OttopilotColumnType.array,
                                valueGetter: (eventContainer: EventContainerWithEvents): Array<string> => {
                                    const value = eventContainer.additionalColumnValues[columnFieldId];
                                    return Array.isArray(value) ? value.map((id) => options.find((option) => option.id === id)?.displayName ?? String(id)) : [];
                                },
                            };
                        }

                        case UdoColumnType.ou: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const ouId = eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (ouId === undefined || ouId === null) {
                                        return "-";
                                    }
                                    return getOuDisplayName !== undefined ? getOuDisplayName(ouId) : ouId;
                                },
                            };
                        }

                        case UdoColumnType.product: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const productId = eventContainer.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (productId === undefined || productId === null) {
                                        return "-";
                                    }
                                    return getProductCatalogueDisplayName !== undefined ? getProductCatalogueDisplayName(productId) : productId;
                                },
                            };
                        }

                        case UdoColumnType.boolean:
                        case UdoColumnType.image:
                        case UdoColumnType.json:
                        case UdoColumnType.ous: {
                            const metadata = eventContainerAdditionalColumns?.find((item) => item.id === columnFieldId);
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (item: EventContainerWithEvents): string =>
                                    metadata === undefined
                                        ? "-"
                                        : formatUserDefinedObjectAdditionalColumnValue(metadata, item.additionalColumnValues[columnFieldId], {
                                              formatTimestamp: (value) => formatDateTime(value),
                                              referenceOptionsByTemplateId: userDefinedObjectOptionsByTemplateId,
                                              resolveUser: (id) => getUserDisplayName(id, users, userTemplates, platformUserDetailsByUserId),
                                              resolveUserTemplate: (id) => getUserRole(id, userTemplates),
                                              resolveOrganizationalUnit: (id) => getOuDisplayName?.(id) ?? "?",
                                              resolveProduct: (id) => getProductCatalogueDisplayName?.(id) ?? "?",
                                              resolveGeography: resolveGeographyAdditionalColumnValue,
                                          }),
                            };
                        }
                    }
                    throw new Error("Unsupported Event Container UDO column type");
                }

                case "OttopilotEvent": {
                    const getEvent = (eventContainer: EventContainerWithEvents) =>
                        eventContainer.events.find((item) => item.templateId === col.eventTemplateId);
                    const eventColumnType = col.type;

                    switch (eventColumnType) {
                        case UdoColumnType.string:
                        case UdoColumnType.indianPhoneNumber:
                        case UdoColumnType.email:
                        case UdoColumnType.uuid: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const event = getEvent(eventContainer);
                                    if (event === undefined) {
                                        return "-";
                                    }
                                    return (event.additionalColumnValues[columnFieldId] as string | undefined | null) ?? "-";
                                },
                            };
                        }

                        case UdoColumnType.geographyCountries:
                        case UdoColumnType.geographyIndiaCities:
                        case UdoColumnType.geographyIndiaPincodes:
                        case UdoColumnType.geographyIndiaStates:
                        case UdoColumnType.geographyIndiaZones: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const event = getEvent(eventContainer);
                                    return event === undefined ? "" : ((event.additionalColumnValues[columnFieldId] as Uuid | undefined | null) ?? "");
                                },
                                valueFormatter: (value: string): string => resolveGeographyAdditionalColumnValue(eventColumnType, value) ?? "-",
                            };
                        }

                        case UdoColumnType.geographyLatitude:
                        case UdoColumnType.geographyLongitude: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const event = getEvent(eventContainer);
                                    if (event === undefined) {
                                        return "";
                                    }
                                    const value = event.additionalColumnValues[columnFieldId];
                                    return typeof value === "number" || typeof value === "string" ? String(value) : "";
                                },
                                valueFormatter: (value: string): string => resolveGeographyAdditionalColumnValue(eventColumnType, value) ?? "-",
                            };
                        }

                        case UdoColumnType.integer:
                        case UdoColumnType.number: {
                            return {
                                ...base,
                                type: OttopilotColumnType.number,
                                valueGetter: (eventContainer: EventContainerWithEvents): number => {
                                    const value = getEvent(eventContainer)?.additionalColumnValues[columnFieldId];
                                    if (typeof value === "number" && Number.isFinite(value)) {
                                        return value;
                                    }
                                    if (typeof value === "string" && value.trim() !== "") {
                                        const parsed = Number(value);
                                        if (Number.isNaN(parsed) !== true) {
                                            return parsed;
                                        }
                                    }
                                    return 0;
                                },
                            };
                        }

                        case UdoColumnType.userDefinedObject: {
                            const eventTemplate = eventTemplates.find((item) => item.id === col.eventTemplateId);
                            const eventColumn = getAdditionalColumns(eventTemplate).find((item) => item.id === columnFieldId);
                            if (eventColumn?.type !== UdoColumnType.userDefinedObject) {
                                return {...base, type: OttopilotColumnType.string, valueGetter: () => ""};
                            }
                            const options = userDefinedObjectOptionsByTemplateId?.get(eventColumn.configuration.userDefinedObjectTemplateId) ?? [];
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const value = getEvent(eventContainer)?.additionalColumnValues[columnFieldId];
                                    return typeof value === "string" ? value : "";
                                },
                                valueFormatter: (value: string): string => options.find((option) => option.id === value)?.displayName ?? "-",
                            };
                        }

                        case UdoColumnType.userDefinedObjects: {
                            const eventTemplate = eventTemplates.find((item) => item.id === col.eventTemplateId);
                            const eventColumn = getAdditionalColumns(eventTemplate).find((item) => item.id === columnFieldId);
                            if (eventColumn?.type !== UdoColumnType.userDefinedObjects) {
                                return {...base, type: OttopilotColumnType.array, valueGetter: () => []};
                            }
                            const options = userDefinedObjectOptionsByTemplateId?.get(eventColumn.configuration.userDefinedObjectTemplateId) ?? [];
                            return {
                                ...base,
                                type: OttopilotColumnType.array,
                                valueGetter: (eventContainer: EventContainerWithEvents): Array<string> => {
                                    const value = getEvent(eventContainer)?.additionalColumnValues[columnFieldId];
                                    return Array.isArray(value) ? value.map((id) => options.find((option) => option.id === id)?.displayName ?? String(id)) : [];
                                },
                            };
                        }

                        case UdoColumnType.timestamp: {
                            return {
                                ...base,
                                type: OttopilotColumnType.iso8601DateTime,
                                valueGetter: (eventContainer: EventContainerWithEvents): Iso8601DateTime => {
                                    const event = getEvent(eventContainer);
                                    if (event === undefined) {
                                        return "" as Iso8601DateTime;
                                    }
                                    const value = event.additionalColumnValues[columnFieldId];
                                    return (typeof value === "string" ? value : "") as Iso8601DateTime;
                                },
                                valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                            };
                        }

                        case UdoColumnType.user: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const event = getEvent(eventContainer);
                                    if (event === undefined) {
                                        return "-";
                                    }
                                    const userId = event.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (userId === undefined || userId === null) {
                                        return "-";
                                    }
                                    return getUserDisplayName(userId, users, userTemplates, platformUserDetailsByUserId);
                                },
                            };
                        }

                        case UdoColumnType.userTemplate: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const event = getEvent(eventContainer);
                                    if (event === undefined) {
                                        return "-";
                                    }
                                    const userId = event.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (userId === undefined || userId === null) {
                                        return "-";
                                    }
                                    return getUserRole(userId, userTemplates);
                                },
                            };
                        }

                        case UdoColumnType.ou: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const event = getEvent(eventContainer);
                                    if (event === undefined) {
                                        return "-";
                                    }
                                    const ouId = event.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (ouId === undefined || ouId === null) {
                                        return "-";
                                    }
                                    return getOuDisplayName !== undefined ? getOuDisplayName(ouId) : ouId;
                                },
                            };
                        }

                        case UdoColumnType.product: {
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (eventContainer: EventContainerWithEvents): string => {
                                    const event = getEvent(eventContainer);
                                    if (event === undefined) {
                                        return "-";
                                    }
                                    const productId = event.additionalColumnValues[columnFieldId] as Uuid | undefined | null;
                                    if (productId === undefined || productId === null) {
                                        return "-";
                                    }
                                    return getProductCatalogueDisplayName !== undefined ? getProductCatalogueDisplayName(productId) : productId;
                                },
                            };
                        }

                        case UdoColumnType.boolean:
                        case UdoColumnType.image:
                        case UdoColumnType.ous:
                        case UdoColumnType.json: {
                            const metadata = getAdditionalColumns(eventTemplates.find((template) => template.id === col.eventTemplateId)).find(
                                (item) => item.id === columnFieldId,
                            );
                            return {
                                ...base,
                                type: OttopilotColumnType.string,
                                valueGetter: (item: EventContainerWithEvents): string =>
                                    metadata === undefined
                                        ? "-"
                                        : formatUserDefinedObjectAdditionalColumnValue(
                                              metadata,
                                              item.events.find((value) => value.templateId === col.eventTemplateId)?.additionalColumnValues[columnFieldId],
                                              {
                                                  formatTimestamp: (value) => formatDateTime(value),
                                                  referenceOptionsByTemplateId: userDefinedObjectOptionsByTemplateId,
                                                  resolveUser: (id) => getUserDisplayName(id, users, userTemplates, platformUserDetailsByUserId),
                                                  resolveUserTemplate: (id) => getUserRole(id, userTemplates),
                                                  resolveOrganizationalUnit: (id) => getOuDisplayName?.(id) ?? "?",
                                                  resolveProduct: (id) => getProductCatalogueDisplayName?.(id) ?? "?",
                                                  resolveGeography: resolveGeographyAdditionalColumnValue,
                                              },
                                          ),
                            };
                        }
                    }
                }
            }
        }

        if (col.columnType === "OttopilotEvent") {
            const getEvent = (eventContainer: EventContainerWithEvents) => eventContainer.events.find((item) => item.templateId === col.eventTemplateId);

            switch (col.field) {
                case "createdBy": {
                    return {
                        ...base,
                        type: OttopilotColumnType.string,
                        valueGetter: (eventContainer: EventContainerWithEvents): string => {
                            const event = getEvent(eventContainer);
                            if (event === undefined) {
                                return "-";
                            }
                            return getUserDisplayName(event.createdBy, users, userTemplates, platformUserDetailsByUserId);
                        },
                    };
                }

                case "updatedBy": {
                    return {
                        ...base,
                        type: OttopilotColumnType.string,
                        valueGetter: (eventContainer: EventContainerWithEvents): string => {
                            const event = getEvent(eventContainer);
                            if (event === undefined) {
                                return "-";
                            }
                            return getUserDisplayName(event.updatedBy, users, userTemplates, platformUserDetailsByUserId);
                        },
                    };
                }

                case "createdAt": {
                    return {
                        ...base,
                        type: OttopilotColumnType.iso8601DateTime,
                        valueGetter: (eventContainer: EventContainerWithEvents): Iso8601DateTime => {
                            const event = getEvent(eventContainer);
                            return event?.createdAt ?? ("" as Iso8601DateTime);
                        },
                        valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                    };
                }

                case "updatedAt": {
                    return {
                        ...base,
                        type: OttopilotColumnType.iso8601DateTime,
                        valueGetter: (eventContainer: EventContainerWithEvents): Iso8601DateTime => {
                            const event = getEvent(eventContainer);
                            return event?.updatedAt ?? ("" as Iso8601DateTime);
                        },
                        valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                    };
                }
            }
        }

        switch (col.field) {
            case "createdBy": {
                return {
                    ...base,
                    type: OttopilotColumnType.string,
                    valueGetter: (eventContainer: EventContainerWithEvents): string =>
                        getUserDisplayName(eventContainer.createdBy, users, userTemplates, platformUserDetailsByUserId),
                };
            }

            case "updatedBy": {
                return {
                    ...base,
                    type: OttopilotColumnType.string,
                    valueGetter: (eventContainer: EventContainerWithEvents): string =>
                        getUserDisplayName(eventContainer.updatedBy, users, userTemplates, platformUserDetailsByUserId),
                };
            }

            case "createdAt": {
                return {
                    ...base,
                    type: OttopilotColumnType.iso8601DateTime,
                    valueGetter: (eventContainer: EventContainerWithEvents): Iso8601DateTime => eventContainer.createdAt,
                    valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                    filteringCapability_contains: true,
                };
            }

            case "updatedAt": {
                return {
                    ...base,
                    type: OttopilotColumnType.iso8601DateTime,
                    valueGetter: (eventContainer: EventContainerWithEvents): Iso8601DateTime => eventContainer.updatedAt,
                    valueFormatter: (value: Iso8601DateTime) => formatDateTime(value),
                    filteringCapability_contains: true,
                };
            }
        }

        return {
            ...base,
            type: OttopilotColumnType.string,
            valueGetter: () => "-",
        };
    });
}
